import { Component } from '@theme/component';
import { prefersReducedMotion, lockScroll, unlockScroll } from '@theme/utilities';

const SESSION_KEY = 'undrWorldBootSeen';
const EXIT_DURATION = 420;

class UndrWorldBoot extends Component {
  requiredRefs = ['startButton', 'status'];

  #timers = [];
  #idleHandle;

  connectedCallback() {
    super.connectedCallback();

    if (this.#shouldSkip()) {
      this.setAttribute('data-dismissed', '');
      this.hidden = true;
      this.#openHub(false);
      window.setTimeout(() => this.#focusHub(), 0);
      return;
    }

    this.#hub()?.setAttribute('data-awaiting-access', '');
    this.setAttribute('data-active', '');
    lockScroll(this);
    this.refs.startButton.focus();
    this.addEventListener('keydown', this.#onKeydown);

    if (!prefersReducedMotion()) {
      this.addEventListener('pointermove', this.#onPointerMove);
      this.addEventListener('pointerleave', this.#resetPointer);
      this.#scheduleModel();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeydown);
    this.removeEventListener('pointermove', this.#onPointerMove);
    this.removeEventListener('pointerleave', this.#resetPointer);
    this.#clearTimers();
    unlockScroll(this);
  }

  activate() {
    if (this.hasAttribute('data-dismissed') || this.hasAttribute('data-transitioning')) return;

    this.setAttribute('data-transitioning', '');
    this.setAttribute('aria-busy', 'true');
    this.refs.startButton.disabled = true;
    this.#setStage('request', this.dataset.labelAccessRequest);

    if (prefersReducedMotion()) {
      this.#setStage('open', this.dataset.labelAccessOpen);
      this.#timers.push(window.setTimeout(() => this.#finish(), 100));
      return;
    }

    this.#queueStage(150, 'found', this.dataset.labelSignalFound);
    this.#queueStage(350, 'sync', this.dataset.labelWorldSync);
    this.#queueStage(700, 'open', this.dataset.labelAccessOpen);
    this.#timers.push(window.setTimeout(() => this.#finish(), 1200));
  }

  #queueStage(delay, stage, label) {
    this.#timers.push(window.setTimeout(() => this.#setStage(stage, label), delay));
  }

  #setStage(stage, label) {
    this.dataset.stage = stage;
    if (label) this.refs.status.textContent = label;
  }

  #finish() {
    if (this.hasAttribute('data-dismissed')) return;

    this.#clearTimers();
    this.setAttribute('data-dismissed', '');
    this.setAttribute('aria-busy', 'false');
    unlockScroll(this);
    this.#remember();
    this.#openHub(true);
    this.querySelector('product-model')?.pauseMedia?.();
    window.setTimeout(() => this.#focusHub(), prefersReducedMotion() ? 0 : EXIT_DURATION);
  }

  #scheduleModel() {
    const model = this.querySelector('product-model');
    if (!model) return;

    const load = async () => {
      await customElements.whenDefined('product-model');
      model.loadContent?.(false);
      const viewer = model.querySelector('model-viewer');
      if (!viewer) return;

      viewer.removeAttribute('camera-controls');
      viewer.removeAttribute('poster');
      viewer.setAttribute('tabindex', '-1');
      viewer.setAttribute('aria-hidden', 'true');

      const markReady = () => this.setAttribute('data-model-ready', '');
      if (viewer.loaded) {
        markReady();
      } else {
        viewer.addEventListener('load', markReady, { once: true });
      }
    };

    if ('requestIdleCallback' in window) {
      this.#idleHandle = window.requestIdleCallback(load, { timeout: 1400 });
    } else {
      this.#idleHandle = window.setTimeout(load, 450);
    }
  }

  #clearTimers() {
    this.#timers.forEach((timer) => window.clearTimeout(timer));
    this.#timers = [];

    if (this.#idleHandle === undefined) return;
    if ('cancelIdleCallback' in window) {
      window.cancelIdleCallback(this.#idleHandle);
    } else {
      window.clearTimeout(this.#idleHandle);
    }
    this.#idleHandle = undefined;
  }

  #shouldSkip() {
    if (document.documentElement.classList.contains('shopify-design-mode')) return true;

    try {
      return sessionStorage.getItem(SESSION_KEY) === '1';
    } catch {
      return false;
    }
  }

  #remember() {
    try {
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch {
      return;
    }
  }

  #hub() {
    return document.querySelector('undr-world-hub-component');
  }

  #openHub(animate) {
    const hub = this.#hub();
    if (!hub) return;
    hub.removeAttribute('data-awaiting-access');
    if (animate) hub.setAttribute('data-access-open', '');
  }

  #focusHub() {
    const hub = this.#hub();
    if (!hub) return;

    hub.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
    hub.querySelector('a, button')?.focus({ preventScroll: true });
  }

  #onPointerMove = (event) => {
    if (this.hasAttribute('data-transitioning')) return;
    const artifact = this.refs.artifact;
    if (!(artifact instanceof HTMLElement)) return;

    const bounds = this.getBoundingClientRect();
    const x = ((event.clientX - bounds.left) / bounds.width - 0.5) * 4;
    const y = ((event.clientY - bounds.top) / bounds.height - 0.5) * -3;
    artifact.style.setProperty('--artifact-rotate-x', `${y.toFixed(2)}deg`);
    artifact.style.setProperty('--artifact-rotate-y', `${x.toFixed(2)}deg`);
  };

  #resetPointer = () => {
    const artifact = this.refs.artifact;
    if (!(artifact instanceof HTMLElement)) return;
    artifact.style.removeProperty('--artifact-rotate-x');
    artifact.style.removeProperty('--artifact-rotate-y');
  };

  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.#setStage('open', this.dataset.labelAccessOpen);
    this.#finish();
  };
}

if (!customElements.get('undr-world-boot-component')) {
  customElements.define('undr-world-boot-component', UndrWorldBoot);
}
